import type { SelectOption } from '@ps/web-shared-components';
import { DropDown, RawInput } from '@ps/web-shared-components';
import { Label } from '@ps/web-shared-ui';
import { format } from 'date-fns';
import { uniq, uniqBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import styles from './EventDetailsFilter.scss';
import type { EventDetails } from './EventDetailsTable';

interface EventFilter {
  events: EventDetails[];
  onFilter(data: EventDetails[]): void;
}

const EventDetailsFilter: React.FC<EventFilter> = ({ events, onFilter }) => {
  const [gameTypeFilter, setGameTypeFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [buyinFilter, setBuyinFilter] = useState('');

  const buyinList = useMemo(() => {
    const result = uniq(events.map((item) => item.buyin))
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map((item) => ({ text: item, value: item }));

    result.unshift({ text: 'Any Buy-In', value: '' });

    return result;
  }, [events]);

  const startDateList = useMemo(() => {
    const data = events;

    const result = uniqBy(
      data.map((item) => ({
        text: format(item.startDate, 'd MMMM'),
        value: item.startDate.toISOString()
      })),
      'text'
    ) as SelectOption[];

    result.unshift({ text: 'Any', value: '' });

    return result;
  }, [events]);

  const endDateList = useMemo(() => {
    const data = events;

    let result = uniqBy(
      data
        .slice()
        .reverse()
        .map((item) => ({
          text: format(item.startDate, 'd MMMM'),
          value: item.startDate.toISOString()
        })),
      'text'
    ) as SelectOption[];

    result = result.slice().reverse();

    result.unshift({ text: 'Any', value: '' });

    return result;
  }, [events]);

  const gameTypeList = useMemo(() => {
    const result = uniq(events.map((item) => item.gameType)).map((item) => ({
      text: item,
      value: item
    })) as SelectOption[];

    result.unshift({ text: 'All Types', value: '' });

    return result;
  }, [events]);

  useEffect(() => {
    let result = events;

    if (gameTypeFilter) {
      result = events.filter((item) => item.gameType === gameTypeFilter);
    }

    if (fieldFilter) {
      result = result.filter((item) =>
        item.title.toLowerCase().includes(fieldFilter.toLowerCase())
      );
    }

    if (startDateFilter) {
      result = result.filter(
        (item) => item.startDate.getTime() >= new Date(startDateFilter).getTime()
      );
    }

    if (endDateFilter) {
      result = result.filter(
        (item) => item.startDate.getTime() <= new Date(endDateFilter).getTime()
      );
    }

    if (buyinFilter) {
      result = result.filter((item) => item.buyin === buyinFilter);
    }

    onFilter(result);
  }, [buyinFilter, endDateFilter, events, fieldFilter, onFilter, startDateFilter, gameTypeFilter]);

  return (
    <div className={styles.filter}>
      <div>
        <Label id="game-type-filter" className={styles.label}>
          Game Type
        </Label>
        <DropDown
          options={gameTypeList}
          id="game-type-filter"
          value={gameTypeFilter}
          onSelect={({ value }) => setGameTypeFilter(value?.toString() || '')}
        />
      </div>
      <div>
        <Label id="buy-in-filter" className={styles.label}>
          Buy-In
        </Label>
        <DropDown
          options={buyinList}
          id="buy-in-filter"
          value={buyinFilter}
          onSelect={({ value }) => setBuyinFilter(value?.toString() || '')}
        />
      </div>
      <div>
        <Label id="start-date-filter" className={styles.label}>
          Start Date
        </Label>
        <DropDown
          options={startDateList}
          id="start-date-filter"
          value={startDateFilter}
          onSelect={({ value }) => setStartDateFilter(value?.toString() || '')}
        />
      </div>
      <div>
        <Label id="end-date-filter" className={styles.label}>
          End Date
        </Label>
        <DropDown
          options={endDateList}
          id="end-date-filter"
          value={endDateFilter}
          onSelect={({ value }) => setEndDateFilter(value?.toString() || '')}
        />
      </div>
      <div className={styles.text}>
        <RawInput
          label="Event Name"
          id="event-name-filter"
          value={fieldFilter}
          onChange={({ target }) => setFieldFilter(target.value)}
        />
      </div>
    </div>
  );
};

export default EventDetailsFilter;
